import React, { type FC } from 'react';
import { FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';

import { useRouteName, useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	PEOPLE_DIRECTORY,
	CONFLUENCE_PEOPLE_DIRECTORY_SEARCH,
} from '@confluence/named-routes/entry-points/routes.ts';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';

import { TeamsIconComponent } from '../GlobalNavigationIcons.tsx';
import { i18n } from '../globalNavigationTranslations.ts';

import { useGlobalItemVisibility } from './useGlobalItemVisibility.ts';
import type { GlobalItemProps } from './globalItemProps.ts';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ '../analyticsCallbacks.ts'))
			.fireTeamsClickedAnalytics,
);

const isOnPeopleRouteArgs = {
	selector: (routeName: string | undefined) => {
		if (!routeName) {
			return false;
		}
		return routeName === PEOPLE_DIRECTORY.name;
	},
};

export const TeamsItem: FC<GlobalItemProps> = ({ isHidden, peekingId, setPeekingId }) => {
	const routeDataRef = useRouteDataRef();
	const isSelected = useRouteName(isOnPeopleRouteArgs);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireClickedAnalytics = useLazyClickAnalytics(createAnalyticsEvent, routeDataRef);

	const shouldHide = useGlobalItemVisibility(
		'teams',
		isSelected,
		isHidden,
		peekingId,
		setPeekingId,
	);

	if (shouldHide) {
		return null;
	}

	return (
		<LinkMenuItem
			href={CONFLUENCE_PEOPLE_DIRECTORY_SEARCH.toUrl()}
			isSelected={isSelected}
			elemBefore={TeamsIconComponent}
			onClick={fireClickedAnalytics}
		>
			<FormattedMessage {...i18n.teams} />
		</LinkMenuItem>
	);
};
